import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const REGION_OPTIONS = [
  { value: "europe",      label: "Flying from Europe",          emoji: "🌍" },
  { value: "asia",        label: "Flying from Asia",            emoji: "🌏" },
  { value: "americas",    label: "Flying from the Americas",    emoji: "🌎" },
  { value: "africa",      label: "Flying from Africa",          emoji: "🌍" },
  { value: "middle_east", label: "Flying from the Middle East", emoji: "🌍" },
  { value: "oceania",     label: "Flying from Oceania",         emoji: "🌏" },
];

const REGION_DISPLAY = {
  all:         "All regions",
  europe:      "Europe",
  asia:        "Asia",
  americas:    "Americas",
  africa:      "Africa",
  middle_east: "Middle East",
  oceania:     "Oceania",
};

export default function PreferencesPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("loading"); // loading | ready | saving | saved | error
  const [email, setEmail] = useState("");
  const [regions, setRegions] = useState(["all"]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing preferences token.");
      setStatus("error");
      return;
    }
    api
      .get("/newsletter/preferences", { params: { token } })
      .then((r) => {
        setEmail(r.data.email);
        setRegions(r.data.regions || ["all"]);
        setStatus("ready");
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "Invalid or expired link.");
        setStatus("error");
      });
  }, [token]);

  const toggleRegion = (value) => {
    if (value === "all") {
      setRegions(["all"]);
    } else {
      setRegions((prev) => {
        const filtered = prev.filter((r) => r !== "all" && r !== value);
        const next = prev.includes(value) ? filtered : [...filtered, value];
        return next.length === 0 ? ["all"] : next;
      });
    }
  };

  const save = async () => {
    setStatus("saving");
    try {
      await api.post("/newsletter/preferences", { token, regions });
      setStatus("saved");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save preferences.");
      setStatus("error");
    }
  };

  const regionSummary = regions.includes("all")
    ? "All regions"
    : regions.map((r) => REGION_DISPLAY[r] || r).join(", ");

  return (
    <div className="bg-[#111111] min-h-screen">
      <Helmet>
        <title>Update Preferences — Restricted Deals</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <SiteHeader />
      <main className="max-w-lg mx-auto px-4 sm:px-6 py-20">

        {status === "loading" && (
          <p className="eyebrow text-[#555]">// LOADING…</p>
        )}

        {status === "error" && (
          <>
            <p className="eyebrow eyebrow-red">// ERROR</p>
            <h1 className="font-mono text-4xl text-white uppercase tracking-tight mt-4">
              Signal invalid
            </h1>
            <p className="mt-4 text-[#9A8F83] font-mono text-sm">{error}</p>
            <Link to="/" className="btn-rd mt-10 inline-flex">← BACK TO DEALS</Link>
          </>
        )}

        {(status === "ready" || status === "saving") && (
          <>
            <p className="eyebrow eyebrow-red">// UPGRADE YOUR CLEARANCE</p>
            <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight mt-4 leading-tight">
              Where are you<br />flying FROM?
            </h1>
            <p className="mt-5 text-[#9A8F83] font-sans leading-relaxed">
              Select your departure region — we'll send you deals departing from your chosen region to anywhere in the world.
            </p>

            <div className="mt-8 border border-[#2A2A2A] bg-[#0E0E0E] px-5 py-5 space-y-3">
              {REGION_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={regions.includes(opt.value)}
                    onChange={() => toggleRegion(opt.value)}
                    className="w-4 h-4 shrink-0 accent-[#CC1A1A]"
                  />
                  <span className="font-mono text-xs tracking-[0.12em] uppercase text-[#E5E0D8]">
                    {opt.emoji} {opt.label}
                  </span>
                </label>
              ))}
              <label className="flex items-center gap-3 cursor-pointer border-t border-[#2A2A2A] pt-3">
                <input
                  type="checkbox"
                  checked={regions.includes("all")}
                  onChange={() => toggleRegion("all")}
                  className="w-4 h-4 shrink-0 accent-[#CC1A1A]"
                />
                <span className="font-mono text-xs tracking-[0.12em] uppercase text-[#9A8F83]">
                  🌐 Send me all deals <span className="text-[#555]">(recommended)</span>
                </span>
              </label>
            </div>

            <button
              onClick={save}
              disabled={status === "saving"}
              className="btn-rd mt-6 w-full justify-center disabled:opacity-60"
            >
              {status === "saving" ? "SAVING…" : "SAVE PREFERENCE →"}
            </button>

            <p className="mt-3 text-[10px] font-mono tracking-widest uppercase text-[#444] text-center">
              {email}
            </p>
          </>
        )}

        {status === "saved" && (
          <>
            <p className="eyebrow eyebrow-red">// PREFERENCES UPDATED</p>
            <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight mt-4 leading-tight">
              Preferences<br />saved.
            </h1>
            <p className="mt-6 text-[#9A8F83] font-sans leading-relaxed">
              You'll now receive alerts for:{" "}
              <strong className="text-white">{regionSummary}</strong>
            </p>
            <Link to="/" className="btn-rd mt-10 inline-flex">← BACK TO DEALS</Link>
          </>
        )}

      </main>
      <SiteFooter />
    </div>
  );
}
